import { collection, getDocs, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export interface FirestoreUser {
  uid: string;
  id: number;
  email: string;
  display_name?: string | null;
  photo_url?: string | null;
  preferred_language?: string;
  plan_name?: string;
  status?: string;
  created_at?: string;
  last_login?: string;
}

export interface FirestorePlan {
  id: number;
  name: string;
  name_ar?: string;
  duration_days: number;
  price_usd: number;
  question_limit?: number | null;
  has_full_access: number;
  has_analytics: number;
  has_priority_support: number;
}

export interface FirestoreSubscription {
  id?: string;
  plan_id: number;
  plan_name: string;
  status: string;
  start_date: string;
  end_date: string;
  created_at?: string;
}

const DEFAULT_PLANS: FirestorePlan[] = [
  { id: 1, name: 'Trial', name_ar: 'تجريبي', duration_days: 10, price_usd: 0, question_limit: null, has_full_access: 1, has_analytics: 1, has_priority_support: 0 },
  { id: 2, name: 'Basic', name_ar: 'أساسي', duration_days: 0, price_usd: 0, question_limit: 20, has_full_access: 0, has_analytics: 0, has_priority_support: 0 },
  { id: 3, name: 'Pro', name_ar: 'احترافي', duration_days: 30, price_usd: 19.99, question_limit: null, has_full_access: 1, has_analytics: 1, has_priority_support: 0 },
  { id: 4, name: 'Enterprise', name_ar: 'مؤسسي', duration_days: 365, price_usd: 199.99, question_limit: null, has_full_access: 1, has_analytics: 1, has_priority_support: 1 },
];

export async function getFirestoreUsers(): Promise<FirestoreUser[]> {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map((d) => ({ ...(d.data() as FirestoreUser) }));
}

export async function getFirestorePlans(): Promise<FirestorePlan[]> {
  const snap = await getDocs(collection(db, 'plans'));
  if (snap.empty) {
    // Seed default plans
    for (const plan of DEFAULT_PLANS) {
      await setDoc(doc(db, 'plans', String(plan.id)), plan);
    }
    return DEFAULT_PLANS;
  }
  return snap.docs.map((d) => ({ ...(d.data() as FirestorePlan) })).sort((a, b) => a.id - b.id);
}

export async function getFirestoreSubscriptions(uid: string): Promise<FirestoreSubscription[]> {
  const snap = await getDocs(collection(db, 'users', uid, 'subscriptions'));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as FirestoreSubscription) }));
}

export function buildBasicSubscription(): FirestoreSubscription {
  const now = new Date();
  const farFuture = new Date(now);
  farFuture.setFullYear(farFuture.getFullYear() + 100);
  return {
    plan_id: 2,
    plan_name: 'Basic',
    status: 'active',
    start_date: now.toISOString(),
    end_date: farFuture.toISOString(),
    created_at: now.toISOString(),
  };
}

export async function downgradeExpiredSubscription(uid: string): Promise<void> {
  const basicSub = buildBasicSubscription();
  const subRef = doc(collection(db, 'users', uid, 'subscriptions'));
  await setDoc(subRef, basicSub);
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    await updateDoc(userRef, {
      plan_name: 'Basic',
      status: 'active',
    });
  }
}

export async function updateFirestoreSubscription(
  uid: string,
  plan: FirestorePlan,
  status: string = 'active',
  startDate?: Date,
  endDate?: Date
): Promise<void> {
  const now = new Date();
  const finalStartDate = startDate ? new Date(startDate) : now;
  const finalEndDate = endDate ? new Date(endDate) : new Date(finalStartDate);
  if (!endDate) {
    if (plan.duration_days > 0) {
      finalEndDate.setDate(finalEndDate.getDate() + plan.duration_days);
    } else {
      finalEndDate.setFullYear(finalEndDate.getFullYear() + 100);
    }
  }

  const subRef = doc(collection(db, 'users', uid, 'subscriptions'));
  await setDoc(subRef, {
    plan_id: plan.id,
    plan_name: plan.name,
    status,
    start_date: finalStartDate.toISOString(),
    end_date: finalEndDate.toISOString(),
    created_at: now.toISOString(),
  });

  // Update user summary
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    await updateDoc(userRef, {
      plan_name: plan.name,
      status,
    });
  }
}
